import React, { useEffect, useState } from "react";
import { PageHeader, Modal, Toast, EmptyState } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import api, { soft } from "@/lib/api";
import Select from "@/components/Select";
import { lock } from "@/lib/perm";

export default function Delegations() {
  const { user, hasRole, has } = useAuth();
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [units, setUnits] = useState([]);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState({text:"",type:"info"});

  const load = async () => {
    const [d,u,o] = await Promise.all([api.get("/planco/delegations"), soft(api.get("/planco/delegations/candidates"), []), soft(api.get("/mdm/org-units"), [])]);
    setRows(d.data); setUsers(u.data); setUnits(o.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/planco/delegations", editing);
      setEditing(null); load();
      setMsg({text:"Delegare creată. Delegatul a fost notificat.",type:"success"}); setTimeout(()=>setMsg({text:""}),2500);
    } catch(e) { setMsg({text:e?.response?.data?.detail||"Eroare",type:"error"}); }
  };
  const revoke = async (id) => { if(!window.confirm("Revocați delegarea?")) return;
    await api.delete(`/planco/delegations/${id}`); load();
    setMsg({text:"Delegare revocată",type:"info"}); setTimeout(()=>setMsg({text:""}),1500);
  };

  const newD = () => {
    const t = new Date().toISOString().slice(0,10);
    setEditing({delegatee_user_id:"", scope_org_unit_id:"", start_date:t, end_date:t, reason:""});
  };

  // Compute my manager scopes for the scope selector (from role_names + roles)
  const myManagerScopes = (user?.roles || []).filter(r => r.role === "manager" && r.scope_org_unit_id).map(r => r.scope_org_unit_id);

  const given = rows.filter(r => r.delegator_user_id === user?.id);
  const received = rows.filter(r => r.delegatee_user_id === user?.id);
  const others = rows.filter(r => r.delegator_user_id !== user?.id && r.delegatee_user_id !== user?.id);

  const isManager = hasRole("manager") || hasRole("admin","hr");

  return (
    <>
      <PageHeader title="Delegare Aprobări" subtitle="Continuitate în absența managerului"
        actions={isManager && <button className="btn btn-primary" onClick={newD} data-testid="add-delegation" {...lock(has("delegations.create"))}>+ Delegare nouă</button>} />
      <Toast msg={msg.text} type={msg.type} />

      {given.length > 0 && (
        <Section title="Delegări acordate de mine" rows={given} units={units} onRevoke={revoke} canRevoke={true} mayRevoke={has("delegations.revoke")} />
      )}
      {received.length > 0 && (
        <Section title="Delegări primite" rows={received} units={units} onRevoke={()=>{}} canRevoke={false} highlight />
      )}
      {hasRole("admin","hr") && others.length > 0 && (
        <Section title="Toate celelalte delegări (vizibile pentru HR/Admin)" rows={others} units={units} onRevoke={revoke} canRevoke={true} mayRevoke={has("delegations.revoke")} />
      )}
      {rows.length===0 && <EmptyState text='Nicio delegare activă. Apasă "+ Delegare nouă" dacă vei lipsi.' />}

      {editing && (
        <Modal title="Delegare nouă" onClose={()=>setEditing(null)}>
          <div style={{display:"grid",gap:12}}>
            <div><label className="label label-req">Deleg către</label>
              <Select className="select" value={editing.delegatee_user_id} onChange={e=>setEditing({...editing,delegatee_user_id:e.target.value})} data-testid="del-user">
                <option value="">— alege —</option>
                {users.filter(u=>u.id!==user?.id && u.active).map(u=><option key={u.id} value={u.id}>{u.last_name} {u.first_name} ({u.username})</option>)}
              </Select></div>
            <div><label className="label">Scope (opțional)</label>
              <Select className="select" value={editing.scope_org_unit_id||""} onChange={e=>setEditing({...editing,scope_org_unit_id:e.target.value})} data-testid="del-scope">
                <option value="">Toate scope-urile mele</option>
                {(hasRole("admin","hr") ? units : units.filter(u => myManagerScopes.includes(u.id))).map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
              </Select>
              <div style={{fontSize:11,color:"#64748B",marginTop:4}}>Dacă lipsește, delegatul primește dreptul de aprobare pe toate scope-urile pe care ești manager.</div>
            </div>
            <div className="grid-2">
              <div><label className="label label-req">Data început</label>
                <input className="input" type="date" value={editing.start_date} onChange={e=>setEditing({...editing,start_date:e.target.value})} data-testid="del-start" /></div>
              <div><label className="label label-req">Data sfârșit</label>
                <input className="input" type="date" value={editing.end_date} onChange={e=>setEditing({...editing,end_date:e.target.value})} data-testid="del-end" /></div>
            </div>
            <div><label className="label">Motiv</label>
              <textarea className="textarea" rows={2} placeholder="ex. Concediu de odihnă / Deplasare" value={editing.reason||""} onChange={e=>setEditing({...editing,reason:e.target.value})} /></div>
          </div>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:18}}>
            <button className="btn btn-secondary" onClick={()=>setEditing(null)}>Anulează</button>
            <button className="btn btn-primary" onClick={save} data-testid="save-delegation" {...lock(has("delegations.create"))}>Delege</button>
          </div>
        </Modal>
      )}
    </>
  );
}

function Section({ title, rows, units, onRevoke, canRevoke, mayRevoke = true, highlight }) {
  return (
    <div className="card fade-up" style={{padding:20, marginBottom:14, borderLeft: highlight?"3px solid var(--blue-primary)":"1px solid #E2E8F0"}}>
      <h3 style={{margin:"0 0 12px",fontSize:14,fontWeight:700,color:"var(--navy)",textTransform:"uppercase",letterSpacing:"0.04em"}}>{title}</h3>
      <table className="tbl">
        <thead><tr><th>Delegant</th><th>Delegat</th><th>Scope</th><th>Perioada</th><th>Motiv</th><th>Status</th><th></th></tr></thead>
        <tbody>{rows.map(d => (
          <tr key={d.id} data-testid={`delegation-${d.id}`}>
            <td>{d.delegator_name}</td>
            <td style={{fontWeight:600}}>{d.delegatee_name}</td>
            <td style={{color:"#64748B",fontSize:12}}>{d.scope_name}</td>
            <td className="tabular-nums">{d.start_date} → {d.end_date}</td>
            <td style={{color:"#64748B",fontSize:12,fontStyle:"italic"}}>{d.reason || "—"}</td>
            <td>{d.is_active_now
              ? <span className="badge badge-active">În vigoare</span>
              : <span className="badge badge-draft">Inactivă</span>}</td>
            <td>{canRevoke && <button className="btn btn-danger" style={{padding:"3px 8px",fontSize:11}} onClick={()=>onRevoke(d.id)} data-testid={`revoke-${d.id}`} {...lock(mayRevoke)}>Revocă</button>}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}
